import React from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
} from '@roger/ui-components';
import { getActiveBillingCycles } from '../../../services/api';
import type {
  ActiveBillingCycleItem,
  EpisodicClaimStatus,
} from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { useBillingCycles } from '../../../hooks/useBillingCycles';

interface EOEPaidEntryProps {
  entry: ActiveBillingCycleItem;
  index?: number;
}

const EOEPaidEntry: React.FC<EOEPaidEntryProps> = ({ entry, index }) => {
  const startDate = formatDateToMMDDYYYY(entry.billing_period_start);
  const endDate = formatDateToMMDDYYYY(entry.billing_period_end);
  const isAlternate = index !== undefined && index % 2 === 1;

  return (
    <TableRow
      className={
        isAlternate
          ? 'bg-muted hover:bg-muted'
          : 'bg-background hover:bg-background'
      }
    >
      <TableCell>{entry.patient_name}</TableCell>
      <TableCell>{entry.patient_mrn}</TableCell>
      <TableCell>{entry.branch}</TableCell>
      <TableCell>
        <span>{startDate} -</span>
        <br />
        <span>{endDate}</span>
      </TableCell>
      <TableCell>{entry.claim_number}</TableCell>
      <TableCell>{entry.batch_id}</TableCell>
      <TableCell>{entry.total_charges}</TableCell>
    </TableRow>
  );
};

const EOEPaidTab: React.FC = () => {
  const { data, isLoading } = useBillingCycles<
    ActiveBillingCycleItem,
    { statuses: EpisodicClaimStatus[] }
  >({
    fetchFn: getActiveBillingCycles,
    fetchPayload: { statuses: ['PAID'] },
    errorMessage: 'Could not fetch data for the "Paid" tab.',
    getId: item => item.billing_cycle_id,
    getName: item => item.patient_name,
  });

  if (isLoading) {
    return (
      <p className='text-muted-foreground text-center py-10'>Loading...</p>
    );
  }

  if (data.length === 0) {
    return (
      <p className='text-muted-foreground text-center py-10'>
        There are currently no records to display
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Patient Name</TableHead>
          <TableHead>MRN</TableHead>
          <TableHead>Branch</TableHead>
          <TableHead>Billing Period</TableHead>
          <TableHead>Claim #</TableHead>
          <TableHead>Batch ID</TableHead>
          <TableHead>Total Charges</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((entry, index) => (
          <EOEPaidEntry
            key={entry.billing_cycle_id}
            entry={entry}
            index={index}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export default EOEPaidTab;
